import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import PaymentModal from "../../components/PaymentModal";
import { useAuth, hasPremiumPlan } from "../../contexts/AuthContext";
import { DEFAULT_CATEGORIES } from "./_shared/categories";
import { toast } from "sonner";
import {
  Home, Users, TrendingUp, TrendingDown, DollarSign,
  ArrowRightLeft, Plus, RefreshCw, Pencil, Trash2,
  CheckCircle2, Circle, Send, Copy, Crown, ChevronDown,
  X, BarChart2, PieChart, Target, LayoutGrid, CreditCard, ArrowLeft, Lock, Gem,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
               "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const ANOS = ["2024","2025","2026","2027"];

// ── KPI Card ───────────────────────────────────────────────────────
const KPI = ({ label, value, sub, color = "emerald", icon: Icon }) => {
  const colors = {
    emerald: "from-emerald-600 to-emerald-800",
    blue:    "from-blue-600 to-blue-800",
    violet:  "from-violet-600 to-violet-800",
    amber:   "from-amber-600 to-amber-800",
    rose:    "from-rose-600 to-rose-800",
    teal:    "from-teal-600 to-teal-800",
  };
  return (
    <div className={`rounded-2xl p-3 md:p-4 bg-gradient-to-br ${colors[color]} text-white relative overflow-hidden`}>
      {Icon && <Icon className="absolute right-3 top-3 h-6 w-6 md:h-8 md:w-8 opacity-20" />}
      <div className="text-[10px] md:text-xs uppercase tracking-widest opacity-80 mb-1 leading-tight pr-6">{label}</div>
      <div className="text-base md:text-2xl font-bold tabular-nums leading-tight">{value}</div>
      {sub && <div className="text-[10px] md:text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
};

// ── Avatar por role ────────────────────────────────────────────────
const Avatar = ({ nome, role }) => (
  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold
    ${role === "titular" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                         : "bg-violet-500/20 text-violet-400 border border-violet-500/30"}`}>
    {(nome || "?")[0].toUpperCase()}
  </span>
);

// ── Modal Criar Família ────────────────────────────────────────────
function CriarFamiliaModal({ user, onClose, onCreated }) {
  const [form, setForm] = useState({
    nome: `Família ${user?.name?.split(" ")[0] || ""}`,
    titularNome: user?.name || "",
    titularEmail: user?.email || "",
    titularRenda: "",
    titularPct: "50",
    conjugeNome: "",
    conjugeEmail: "",
    conjugeRenda: "",
    conjugePct: "50",
  });
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  const handlePct = (field, val) => {
    const n = Math.min(100, Math.max(0, Number(val)));
    const other = field === "titularPct" ? "conjugePct" : "titularPct";
    setForm(f => ({ ...f, [field]: String(n), [other]: String(100 - n) }));
  };

  const submit = async () => {
    if (!form.conjugeNome || !form.conjugeEmail) {
      toast.error("Preencha nome e e-mail do cônjuge");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/familia/criar`, {
        nome: form.nome,
        titular: {
          nome: form.titularNome, email: form.titularEmail,
          renda: Number(form.titularRenda) || 0,
          percentual_padrao: Number(form.titularPct),
        },
        conjuge: {
          nome: form.conjugeNome, email: form.conjugeEmail,
          renda: Number(form.conjugeRenda) || 0,
          percentual_padrao: Number(form.conjugePct),
        },
      });
      setInviteLink(data.invite_link);
      onCreated(data.familia);
      toast.success("Família criada! Convite enviado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar família");
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500";

  if (inviteLink) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-lg font-bold text-white mb-2">✅ Família criada!</h2>
        <p className="text-slate-400 text-sm mb-4">
          Compartilhe o link de convite com {form.conjugeNome}:
        </p>
        <div className="bg-slate-800 rounded-lg p-3 text-xs text-emerald-400 break-all mb-4">{inviteLink}</div>
        <button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Link copiado!"); }}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-2.5 text-sm font-semibold mb-2">
          <Copy className="h-4 w-4" /> Copiar Link
        </button>
        <button onClick={onClose}
          className="w-full text-slate-400 hover:text-white text-sm py-2">
          Fechar
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Home className="h-5 w-5 text-emerald-400" /> Criar Conta Família
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nome da Família</label>
            <input className={inp} value={form.nome}
              onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
          </div>

          <div className="border-t border-slate-800 pt-4">
            <p className="text-xs text-blue-400 font-semibold uppercase mb-3">Seus dados (Titular)</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400 mb-1 block">Nome</label>
                <input className={inp} value={form.titularNome}
                  onChange={e => setForm(f => ({ ...f, titularNome: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">E-mail</label>
                <input className={inp} value={form.titularEmail}
                  onChange={e => setForm(f => ({ ...f, titularEmail: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Renda Mensal</label>
                <input className={inp} type="number" placeholder="0,00" value={form.titularRenda}
                  onChange={e => setForm(f => ({ ...f, titularRenda: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">% Participação</label>
                <input className={inp} type="number" min="0" max="100" value={form.titularPct}
                  onChange={e => handlePct("titularPct", e.target.value)} /></div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-4">
            <p className="text-xs text-violet-400 font-semibold uppercase mb-3">Dados do Cônjuge</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400 mb-1 block">Nome</label>
                <input className={inp} value={form.conjugeNome}
                  onChange={e => setForm(f => ({ ...f, conjugeNome: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">E-mail</label>
                <input className={inp} type="email" value={form.conjugeEmail}
                  onChange={e => setForm(f => ({ ...f, conjugeEmail: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Renda Mensal</label>
                <input className={inp} type="number" placeholder="0,00" value={form.conjugeRenda}
                  onChange={e => setForm(f => ({ ...f, conjugeRenda: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">% Participação</label>
                <input className={inp} type="number" min="0" max="100" value={form.conjugePct}
                  onChange={e => handlePct("conjugePct", e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 bg-blue-500/20 h-2 rounded-full">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${form.titularPct}%` }} />
              </div>
              <span className="text-xs text-slate-400">{form.titularPct}% / {form.conjugePct}%</span>
              <div className="flex-1 bg-violet-500/20 h-2 rounded-full">
                <div className="bg-violet-500 h-2 rounded-full" style={{ width: `${form.conjugePct}%` }} />
              </div>
            </div>
          </div>
        </div>

        <button onClick={submit} disabled={loading}
          className="mt-5 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl py-3 font-semibold">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Criando..." : "Criar e Enviar Convite"}
        </button>
      </div>
    </div>
  );
}

// ── Modal Participação ─────────────────────────────────────────────
function ParticipacaoModal({ familiaId, transacao, membros, onClose, onSaved }) {
  const total = Number(transacao?.valor || 0);
  const [pcts, setPcts] = useState(() => {
    const base = 100 / (membros.length || 1);
    return membros.map(m => ({ userId: m.userId, nome: m.nome, pct: base }));
  });

  const handlePct = (idx, val) => {
    const n = Math.min(100, Math.max(0, Number(val)));
    const rest = 100 - n;
    const others = pcts.filter((_, i) => i !== idx);
    const newPcts = pcts.map((p, i) => {
      if (i === idx) return { ...p, pct: n };
      return { ...p, pct: others.length > 0 ? rest / others.length : 0 };
    });
    setPcts(newPcts);
  };

  const save = async () => {
    try {
      await axios.post(`${API}/familia/participacao`, {
        familiaId,
        transacaoId: transacao.id,
        tipo: transacao.tipo || "gasto_variavel",
        userId_dono: transacao.userId,
        participacoes: pcts.map(p => ({
          userId: p.userId,
          percentual: p.pct,
          valor: (total * p.pct) / 100,
        })),
      });
      toast.success("Participação salva!");
      onSaved();
      onClose();
    } catch {
      toast.error("Erro ao salvar participação");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white">Participação</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-400 mb-4">{transacao?.descricao} — {brl(total)}</p>
        <div className="space-y-3">
          {pcts.map((p, i) => (
            <div key={p.userId} className="flex items-center gap-3">
              <Avatar nome={p.nome} role={i === 0 ? "titular" : "membro"} />
              <span className="flex-1 text-sm text-white">{p.nome}</span>
              <input type="number" min="0" max="100" value={Math.round(p.pct)}
                onChange={e => handlePct(i, e.target.value)}
                className="w-16 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-white text-center" />
              <span className="text-xs text-slate-400 w-20 text-right">{brl((total * p.pct) / 100)}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 border border-slate-700 text-slate-400 rounded-xl py-2 text-sm">
            Cancelar
          </button>
          <button onClick={save}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl py-2 text-sm font-semibold">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aba Painel ─────────────────────────────────────────────────────
function PainelTab({ painel, familia }) {
  if (!painel) return (
    <div className="text-center py-20 text-slate-500">
      <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
      Carregando painel...
    </div>
  );

  const membros = painel.membros || [];
  const titular = membros.find(m => m.role === "titular");
  const conjuge = membros.find(m => m.role === "membro");
  const meuId   = titular?.userId;

  const rep    = painel.repassar || {};
  const meuRep = rep[meuId] || {};
  const aReceber   = meuRep.saldo > 0 ? meuRep.saldo : 0;
  const aRepassar  = meuRep.saldo < 0 ? Math.abs(meuRep.saldo) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPI label="Total de Entradas"      value={brl(painel.total_receitas)} color="emerald" icon={DollarSign} />
        <KPI label={`Pago por ${titular?.nome || "Você"}`}   value={brl(titular?.total_gastos)} color="blue"    icon={TrendingDown} />
        <KPI label={`Pago por ${conjuge?.nome || "Cônjuge"}`} value={brl(conjuge?.total_gastos)} color="violet"  icon={TrendingDown} />
        <KPI label="Custo Total do Mês"     value={brl(painel.total_gastos)}   color="amber"   icon={BarChart2} />
        <KPI label="A Receber"              value={brl(aReceber)}               color="teal"    icon={TrendingUp} />
        <KPI label="A Repassar"             value={brl(aRepassar)}              color="rose"    icon={ArrowRightLeft} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Receitas por pessoa */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-400" /> Entradas por Membro
          </h3>
          {membros.map((m, i) => {
            const pct = painel.total_receitas > 0
              ? ((m.total_entradas / painel.total_receitas) * 100).toFixed(1)
              : "0.0";
            return (
              <div key={m.userId} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Avatar nome={m.nome} role={m.role} />
                    <span className="text-sm text-white">{m.nome}</span>
                  </div>
                  <span className="text-sm font-bold text-white">{brl(m.total_entradas)}
                    <span className="text-xs text-slate-400 ml-1">({pct}%)</span>
                  </span>
                </div>
                <div className={`h-2 rounded-full ${i === 0 ? "bg-blue-500/20" : "bg-violet-500/20"}`}>
                  <div className={`h-2 rounded-full ${i === 0 ? "bg-blue-500" : "bg-violet-500"}`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Gastos por pessoa */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-rose-400" /> Gastos por Membro
          </h3>
          {membros.map((m, i) => {
            const pct = painel.total_gastos > 0
              ? ((m.total_gastos / painel.total_gastos) * 100).toFixed(1)
              : "0.0";
            return (
              <div key={m.userId} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Avatar nome={m.nome} role={m.role} />
                    <span className="text-sm text-white">{m.nome}</span>
                  </div>
                  <span className="text-sm font-bold text-white">{brl(m.total_gastos)}
                    <span className="text-xs text-slate-400 ml-1">({pct}%)</span>
                  </span>
                </div>
                <div className={`h-2 rounded-full ${i === 0 ? "bg-blue-500/20" : "bg-violet-500/20"}`}>
                  <div className={`h-2 rounded-full ${i === 0 ? "bg-blue-500" : "bg-violet-500"}`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Acerto do mês */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-amber-400" /> Acerto do Mês
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.values(rep).map(r => (
            <div key={r.nome}
              className={`rounded-xl p-4 border ${r.saldo >= 0
                ? "border-emerald-500/30 bg-emerald-500/10"
                : "border-rose-500/30 bg-rose-500/10"}`}>
              <div className="text-sm text-slate-300 mb-1">{r.nome}</div>
              <div className={`text-lg font-bold ${r.saldo >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {r.saldo >= 0 ? "A receber" : "A repassar"}: {brl(Math.abs(r.saldo))}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Pagou {brl(r.pagou)} · Deveria {brl(r.deveria)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Aba Gastos ─────────────────────────────────────────────────────
const GASTOS_CAT_COLORS = {
  blue:   { iconBg: "bg-blue-500/15",   iconText: "text-blue-400"   },
  violet: { iconBg: "bg-violet-500/15", iconText: "text-violet-400" },
  rose:   { iconBg: "bg-rose-500/15",   iconText: "text-rose-400"   },
};

const GASTOS_CATS = [
  {
    key: "fixos",
    title: "Gastos Fixos",
    sub: "Contas e compromissos fixos",
    Icon: LayoutGrid,
    color: "blue",
    navTo: "/app/gastos-fixos",
    tipoKey: "gastos_fixos",
    tipo: "gasto_fixo",
    pagoTipo: "fixo",      // used by the toggle-pago endpoint
  },
  {
    key: "variaveis",
    title: "Gastos Variáveis",
    sub: "Despesas do dia a dia",
    Icon: TrendingDown,
    color: "violet",
    navTo: "/app/gastos-variaveis",
    tipoKey: "gastos_variaveis",
    tipo: "gasto_variavel",
    pagoTipo: "variavel",
  },
  {
    key: "cartoes",
    title: "Cartões de Crédito",
    sub: "Faturas e lançamentos",
    Icon: CreditCard,
    color: "rose",
    navTo: "/app/cartoes",
    tipoKey: "cartoes_gastos",
    tipo: "cartao",
    pagoTipo: null,        // cartão pago is tracked per-fatura, not per-item
  },
];

// Formata ISO "YYYY-MM-DD" → "DD/MM/YYYY"
const fmtDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${(d || "").slice(0, 2)}/${m}/${y}`;
};

// Resolve category name from id (falls back to the raw id string)
const catDisplayName = (catId) =>
  DEFAULT_CATEGORIES.find(c => c.id === catId)?.name || catId || "—";

function GastosTab({ painel, familiaId, membros, onRefresh }) {
  const navigate = useNavigate();
  const [view, setView]           = useState(null);
  const [filtro, setFiltro]       = useState("todos");
  const [partModal, setPartModal] = useState(null);
  const [toggling, setToggling]   = useState(null); // item id being toggled

  const totais = GASTOS_CATS.reduce((acc, cat) => {
    acc[cat.key] = (painel?.membros || []).reduce((s, m) =>
      s + (m[cat.tipoKey] || []).reduce((ss, g) => ss + Number(g.valor || 0), 0), 0);
    return acc;
  }, {});

  const titular = membros.find(m => m.role === "titular");

  const togglePago = async (g) => {
    if (!g.pagoTipo) return; // cartão — sem suporte por item
    if (toggling === g.id) return;
    setToggling(g.id);
    try {
      await axios.patch(`${API}/familia/${familiaId}/item/pago`, {
        userId_dono: g.userId,
        tipo: g.pagoTipo,
        itemId: g.id,
        pago: !g.pago,
      });
      onRefresh();
    } catch {
      toast.error("Erro ao atualizar status");
    } finally {
      setToggling(null);
    }
  };

  // ── Cards view ──
  if (!view) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {GASTOS_CATS.map(cat => {
          const { iconBg, iconText } = GASTOS_CAT_COLORS[cat.color];
          return (
            <button key={cat.key}
              onClick={() => { setView(cat.key); setFiltro("todos"); }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-left hover:border-slate-600 transition cursor-pointer">
              <div className={`h-10 w-10 rounded-xl ${iconBg} grid place-items-center mb-4`}>
                <cat.Icon className={`h-5 w-5 ${iconText}`} />
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">{cat.title}</div>
              <div className="text-xl font-bold text-white tabular-nums mb-1">{brl(totais[cat.key])}</div>
              <div className="text-xs text-slate-500">{cat.sub}</div>
            </button>
          );
        })}
      </div>
    );
  }

  // ── Table view ──
  const cfg = GASTOS_CATS.find(c => c.key === view);
  const allItems = (painel?.membros || []).flatMap(m =>
    (m[cfg.tipoKey] || []).map(g => ({
      ...g, tipo: cfg.tipo, pagoTipo: cfg.pagoTipo,
      userId: m.userId, nomeUser: m.nome, role: m.role,
    }))
  ).sort((a, b) => (b.data || "").localeCompare(a.data || ""));

  const filtered = filtro === "todos" ? allItems
    : filtro === "meu"     ? allItems.filter(g => g.userId === titular?.userId)
    : allItems.filter(g => g.userId !== titular?.userId);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => setView(null)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm transition">
            <ArrowLeft className="h-4 w-4" /> Voltar
          </button>
          <span className="text-white font-semibold">{cfg.title}</span>
        </div>
        <button onClick={() => navigate(cfg.navTo)}
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
          <Plus className="h-3.5 w-3.5" /> Novo
        </button>
      </div>

      {/* Filtros */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1">
          {[["todos","Todos"],["meu","Só meus"],["conjuge","Só do cônjuge"]].map(([v,l]) => (
            <button key={v} onClick={() => setFiltro(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition
                ${filtro === v ? "bg-emerald-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={onRefresh}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
          <RefreshCw className="h-3.5 w-3.5" /> Recarregar
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase">
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-left">Quem</th>
                <th className="px-4 py-3 text-left">Descrição</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3 text-center">Pago?</th>
                <th className="px-4 py-3 text-center">Participação</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">Nenhum gasto encontrado</td></tr>
              )}
              {filtered.map((g, i) => (
                <tr key={g.id || i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDateBR(g.data)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar nome={g.nomeUser} role={g.role} />
                      <span className="text-slate-300 text-xs whitespace-nowrap">{g.nomeUser}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white">{g.desc || "—"}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{catDisplayName(g.cat)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-rose-400 whitespace-nowrap">{brl(g.valor)}</td>
                  <td className="px-4 py-3 text-center">
                    {g.pagoTipo ? (
                      <button
                        onClick={() => togglePago(g)}
                        disabled={toggling === g.id}
                        title={g.pago ? "Marcar como pendente" : "Marcar como pago"}
                        className={`mx-auto flex items-center justify-center w-7 h-7 rounded-full transition
                          ${g.pago
                            ? "bg-emerald-500/20 hover:bg-emerald-500/40"
                            : "bg-slate-700 hover:bg-slate-600"}
                          ${toggling === g.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                        {g.pago
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          : <Circle className="h-4 w-4 text-slate-400" />}
                      </button>
                    ) : (
                      <span className="text-slate-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setPartModal({ ...g })}
                      className="px-2 py-1 bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 rounded text-xs">
                      %
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {partModal && (
        <ParticipacaoModal
          familiaId={familiaId}
          transacao={partModal}
          membros={membros}
          onClose={() => setPartModal(null)}
          onSaved={onRefresh}
        />
      )}
    </div>
  );
}

// ── Aba Entradas ───────────────────────────────────────────────────
function ReceitasTab({ painel }) {
  const titular = painel?.membros?.find(m => m.role === "titular");
  const conjuge = painel?.membros?.find(m => m.role === "membro");
  const total   = painel?.total_receitas || 0;

  const allEnt = (painel?.membros || []).flatMap(m =>
    (m.entradas || []).map(e => ({ ...e, nomeUser: m.nome, role: m.role }))
  ).sort((a, b) => (b.data || "").localeCompare(a.data || ""));

  const pctT = total > 0 ? (((titular?.total_entradas||0)/total)*100).toFixed(1) : "0.0";
  const pctC = total > 0 ? (((conjuge?.total_entradas||0)/total)*100).toFixed(1) : "0.0";

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <KPI label={`Entradas de ${titular?.nome||"Você"}`}   value={brl(titular?.total_entradas)} sub={`${pctT}% do total`} color="blue"   icon={TrendingUp} />
        <KPI label={`Entradas de ${conjuge?.nome||"Cônjuge"}`} value={brl(conjuge?.total_entradas)} sub={`${pctC}% do total`} color="violet" icon={TrendingUp} />
        <KPI label="Total de Entradas"                          value={brl(total)}                                              color="amber"  icon={DollarSign} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase">
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Recebido por</th>
              <th className="px-4 py-3 text-left">Descrição</th>
              <th className="px-4 py-3 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {allEnt.length === 0 && (
              <tr><td colSpan={4} className="text-center py-10 text-slate-500">Nenhuma receita encontrada</td></tr>
            )}
            {allEnt.map((e, i) => (
              <tr key={e.id || i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                <td className="px-4 py-3 text-slate-400 text-xs">{e.data || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Avatar nome={e.nomeUser} role={e.role} />
                    <span className="text-slate-300 text-xs">{e.nomeUser}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-white">{e.descricao || "—"}</td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-400">{brl(e.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Aba Saldos ─────────────────────────────────────────────────────
function SaldosTab({ familiaId, ano }) {
  const [saldos, setSaldos] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/familia/${familiaId}/saldos?ano=${ano}`);
      setSaldos(data);
    } catch { toast.error("Erro ao carregar saldos"); }
    finally { setLoading(false); }
  }, [familiaId, ano]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleAcerto = async (mes, atual) => {
    try {
      await axios.post(`${API}/familia/acerto`, { familiaId, mes, ano, concluido: !atual });
      fetch();
    } catch { toast.error("Erro ao salvar acerto"); }
  };

  if (loading || !saldos) return <div className="text-center py-20 text-slate-500"><RefreshCw className="h-6 w-6 mx-auto animate-spin mb-2" />Carregando saldos...</div>;

  const familia = saldos.familia;
  const meses   = saldos.meses || [];
  const membros = familia?.membros?.filter(m => m.userId) || [];

  const totaisPorMembro = membros.map(m => ({
    ...m,
    totalSaldo: meses.reduce((s, mes) => {
      const mm = mes.membros.find(mm => mm.userId === m.userId);
      return s + (mm?.saldo || 0);
    }, 0),
  }));

  return (
    <div>
      {/* Banners de totais */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {totaisPorMembro.map((m, i) => (
          <div key={m.userId}
            className={`rounded-xl p-4 font-bold text-white text-center
              ${m.totalSaldo >= 0 ? "bg-emerald-700" : "bg-rose-700"}`}>
            {m.nome}: {m.totalSaldo >= 0 ? "A receber" : "A repassar"} {brl(Math.abs(m.totalSaldo))} em {ano}
          </div>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase">
              <th className="px-4 py-3 text-left">Mês</th>
              <th className="px-4 py-3 text-right">Custo Total</th>
              <th className="px-4 py-3 text-center">Acerto Feito?</th>
              {membros.map(m => (
                <th key={m.userId} className="px-4 py-3 text-right">{m.nome}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {meses.map(mes => (
              <tr key={mes.mes}
                className={`border-b border-slate-800/50 transition
                  ${mes.acerto_concluido ? "opacity-40" : "hover:bg-slate-800/30"}`}>
                <td className="px-4 py-3 text-slate-300">{mes.mes_nome}/{ano.slice(2)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-white">{brl(mes.total_gastos)}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleAcerto(mes.mes, mes.acerto_concluido)}
                    className="text-slate-400 hover:text-emerald-400 transition">
                    {mes.acerto_concluido
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mx-auto" />
                      : <Circle className="h-5 w-5 mx-auto" />}
                  </button>
                </td>
                {membros.map(m => {
                  const mm = mes.membros.find(mm => mm.userId === m.userId);
                  const s  = mm?.saldo || 0;
                  return (
                    <td key={m.userId}
                      className={`px-4 py-3 text-right tabular-nums font-semibold
                        ${s > 0 ? "text-emerald-400 bg-emerald-500/5"
                        : s < 0 ? "text-rose-400 bg-rose-500/5" : "text-slate-500"}`}>
                      {s !== 0 ? brl(s) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers de meta ────────────────────────────────────────────────
const normCat = (s) => (s || "").toLowerCase().trim();

const getMetaStyle = (pct) => {
  if (pct >= 100) return { bar: "bg-rose-500",    track: "bg-rose-500/20",    text: "text-rose-400",    label: "Ultrapassado",    emoji: "🔴" };
  if (pct >= 91)  return { bar: "bg-orange-500",  track: "bg-orange-500/20",  text: "text-orange-400",  label: "Quase no limite", emoji: "🔴" };
  if (pct >= 71)  return { bar: "bg-yellow-500",  track: "bg-yellow-500/20",  text: "text-yellow-400",  label: "Atenção",         emoji: "⚠️" };
  return              { bar: "bg-emerald-500",  track: "bg-emerald-500/20",  text: "text-emerald-400", label: "Dentro do limite","emoji": "✅" };
};

// ── Aba Metas ──────────────────────────────────────────────────────
function MetasTab({ familiaId, painel }) {
  const [metas, setMetas]             = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [nova, setNova]               = useState({ categoria: "", valor: "" });
  const [saving, setSaving]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // meta object to delete
  const [editMeta, setEditMeta]       = useState(null);     // meta object being edited
  const [editForm, setEditForm]       = useState({ categoria: "", valor: "" });
  const [editSaving, setEditSaving]   = useState(false);

  const fetchMetas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/familia/${familiaId}/metas`);
      setMetas(data.metas || []);
    } catch {}
  }, [familiaId]);

  useEffect(() => { fetchMetas(); }, [fetchMetas]);

  // Soma gastos fixos + variáveis + cartões dos dois membros, normalizado por categoria
  const gastosPorCat = {};
  (painel?.membros || []).forEach(m => {
    [...(m.gastos_fixos||[]), ...(m.gastos_variaveis||[]), ...(m.cartoes_gastos||[])].forEach(g => {
      const cat = normCat(g.categoria || "outros");
      gastosPorCat[cat] = (gastosPorCat[cat] || 0) + Number(g.valor || 0);
    });
  });

  const getGasto = (categoria) => gastosPorCat[normCat(categoria)] || 0;
  const getPct   = (m) => m.valor_mensal > 0 ? (getGasto(m.categoria) / m.valor_mensal) * 100 : 0;

  // Alertas
  const metasAlerta       = metas.map(m => ({ ...m, pct: getPct(m) })).filter(m => m.pct >= 90);
  const metasUltrapassadas = metasAlerta.filter(m => m.pct >= 100);
  const metasAtencao       = metasAlerta.filter(m => m.pct >= 90 && m.pct < 100);

  const criar = async () => {
    if (!nova.categoria || !nova.valor) return toast.error("Preencha categoria e limite");
    setSaving(true);
    try {
      await axios.post(`${API}/familia/${familiaId}/metas`, {
        categoria: nova.categoria, valor_mensal: Number(nova.valor),
      });
      setNova({ categoria: "", valor: "" });
      setShowModal(false);
      fetchMetas();
      toast.success("Meta criada!");
    } catch { toast.error("Erro ao criar meta"); }
    finally { setSaving(false); }
  };

  const deletar = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/familia/${familiaId}/metas/${confirmDelete.metaId}`);
      setConfirmDelete(null);
      fetchMetas();
    } catch { toast.error("Erro ao remover meta"); }
  };

  const abrirEditar = (m) => {
    setEditMeta(m);
    setEditForm({ categoria: m.categoria, valor: String(m.valor_mensal) });
  };

  const salvarEditar = async () => {
    if (!editForm.categoria || !editForm.valor) return toast.error("Preencha todos os campos");
    setEditSaving(true);
    try {
      await axios.put(`${API}/familia/${familiaId}/metas/${editMeta.metaId}`, {
        categoria: editForm.categoria, valor_mensal: Number(editForm.valor),
      });
      setEditMeta(null);
      fetchMetas();
      toast.success("Meta atualizada!");
    } catch { toast.error("Erro ao atualizar meta"); }
    finally { setEditSaving(false); }
  };

  const inp = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500";

  return (
    <div>
      {/* Banner vermelho: metas ultrapassadas */}
      {metasUltrapassadas.length > 0 && (
        <div className="mb-4 flex items-start gap-3 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">
          <span className="text-lg leading-none mt-0.5">🔴</span>
          <div>
            <div className="text-rose-400 font-semibold text-sm">Meta(s) ultrapassada(s) este mês!</div>
            <div className="text-rose-300/70 text-xs mt-0.5">
              {metasUltrapassadas.map(m => m.categoria).join(", ")} — limite excedido
            </div>
          </div>
        </div>
      )}

      {/* Banner amarelo: atenção 90-99% */}
      {metasAtencao.length > 0 && (
        <div className="mb-4 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <span className="text-lg leading-none mt-0.5">⚠️</span>
          <div>
            <div className="text-amber-400 font-semibold text-sm">Atenção: metas próximas do limite</div>
            <div className="text-amber-300/70 text-xs mt-0.5">
              {metasAtencao.map(m => `${m.categoria} (${m.pct.toFixed(0)}%)`).join(", ")}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300">Limite máximo mensal por categoria</h3>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
          <Plus className="h-3.5 w-3.5" /> Nova Meta
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase">
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-right">Limite Mensal</th>
                <th className="px-4 py-3 text-right">Gasto Atual</th>
                <th className="px-4 py-3 text-right">% Atingido</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-left min-w-[100px]">Progresso</th>
                <th className="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {metas.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">Nenhuma meta cadastrada</td></tr>
              )}
              {metas.map(m => {
                const gasto  = getGasto(m.categoria);
                const rawPct = getPct(m);
                const style  = getMetaStyle(rawPct);
                return (
                  <tr key={m.metaId} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                    <td className="px-4 py-3 text-white font-medium">{m.categoria}</td>
                    <td className="px-4 py-3 text-right text-slate-300 whitespace-nowrap">{brl(m.valor_mensal)}</td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${style.text}`}>{brl(gasto)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${style.text}`}>{rawPct.toFixed(0)}%</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap ${style.text}`}>
                        {style.emoji} {style.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 min-w-[100px]">
                      <div className={`h-2 rounded-full w-full ${style.track}`}>
                        <div className={`h-2 rounded-full transition-all ${style.bar}`}
                          style={{ width: `${Math.min(rawPct, 100)}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => abrirEditar(m)}
                          className="text-slate-500 hover:text-blue-400 transition"
                          title="Editar meta">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(m)}
                          className="text-slate-500 hover:text-rose-400 transition"
                          title="Excluir meta">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Nova Meta */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Target className="h-4 w-4 text-emerald-400" /> Nova Meta
              </h3>
              <button onClick={() => setShowModal(false)}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Categoria</label>
                <select className={inp} value={nova.categoria}
                  onChange={e => setNova(n => ({ ...n, categoria: e.target.value }))}>
                  <option value="">Selecione uma categoria…</option>
                  {DEFAULT_CATEGORIES.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                  <option value="Outros">Outros</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Limite mensal (R$)</label>
                <input type="number" min="0" step="0.01" placeholder="Ex: 1500,00"
                  className={inp} value={nova.valor}
                  onChange={e => setNova(n => ({ ...n, valor: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowModal(false)}
                className="flex-1 border border-slate-700 text-slate-400 rounded-xl py-2.5 text-sm">
                Cancelar
              </button>
              <button onClick={criar} disabled={saving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white">Excluir meta?</h3>
              <button onClick={() => setConfirmDelete(null)}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <p className="text-slate-400 text-sm mb-5">
              Tem certeza que deseja excluir a meta de{" "}
              <strong className="text-white">{confirmDelete.categoria}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-slate-700 text-slate-400 rounded-xl py-2.5 text-sm">
                Cancelar
              </button>
              <button onClick={deletar}
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white rounded-xl py-2.5 text-sm font-semibold">
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Meta */}
      {editMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Pencil className="h-4 w-4 text-blue-400" /> Editar Meta
              </h3>
              <button onClick={() => setEditMeta(null)}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Categoria</label>
                <select className={inp} value={editForm.categoria}
                  onChange={e => setEditForm(f => ({ ...f, categoria: e.target.value }))}>
                  <option value="">Selecione uma categoria…</option>
                  {DEFAULT_CATEGORIES.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                  <option value="Outros">Outros</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Limite mensal (R$)</label>
                <input type="number" min="0" step="0.01" placeholder="Ex: 1500,00"
                  className={inp} value={editForm.valor}
                  onChange={e => setEditForm(f => ({ ...f, valor: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditMeta(null)}
                className="flex-1 border border-slate-700 text-slate-400 rounded-xl py-2.5 text-sm">
                Cancelar
              </button>
              <button onClick={salvarEditar} disabled={editSaving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                {editSaving ? "Salvando…" : "Salvar alterações"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Aba Configurações ──────────────────────────────────────────────
function ConfigTab({ familia, familiaId, onUpdated, onDeleted }) {
  const [form, setForm]     = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!familia) return;
    setForm({
      nome: familia.nome,
      membros: familia.membros.map(m => ({ ...m })),
    });
  }, [familia]);

  if (!form) return null;

  const updateMembro = (idx, field, val) => {
    const membros = [...form.membros];
    membros[idx] = { ...membros[idx], [field]: val };
    // Ajustar percentuais para somar 100 entre os 2 membros
    if (field === "percentual_padrao" && membros.length === 2) {
      const other = idx === 0 ? 1 : 0;
      membros[other] = { ...membros[other], percentual_padrao: 100 - Number(val) };
    }
    setForm(f => ({ ...f, membros }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/familia/${familiaId}/configuracoes`, {
        nome: form.nome, membros: form.membros,
      });
      onUpdated(data.familia);
      toast.success("Configurações salvas!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const deletar = async () => {
    try {
      await axios.delete(`${API}/familia/${familiaId}`);
      toast.success("Família apagada");
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao apagar");
    }
  };

  const inp = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500";

  return (
    <div className="space-y-5 max-w-xl">
      <div>
        <label className="text-xs text-slate-400 mb-1 block">Nome da Família</label>
        <input className={inp} value={form.nome}
          onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase">
              <th className="px-4 py-3 text-left">Membro</th>
              <th className="px-4 py-3 text-left">E-mail</th>
              <th className="px-4 py-3 text-right">Renda</th>
              <th className="px-4 py-3 text-right">%</th>
            </tr>
          </thead>
          <tbody>
            {form.membros.map((m, i) => (
              <tr key={i} className="border-b border-slate-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Avatar nome={m.nome} role={m.role} />
                    <input className="bg-transparent text-white text-sm w-28 focus:outline-none border-b border-transparent focus:border-slate-600"
                      value={m.nome} onChange={e => updateMembro(i, "nome", e.target.value)} />
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400 text-xs">{m.email}</td>
                <td className="px-4 py-3">
                  <input type="number" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white w-24 text-right"
                    value={m.renda} onChange={e => updateMembro(i, "renda", Number(e.target.value))} />
                </td>
                <td className="px-4 py-3">
                  <input type="number" min="0" max="100"
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white w-16 text-right"
                    value={m.percentual_padrao}
                    onChange={e => updateMembro(i, "percentual_padrao", Number(e.target.value))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
          Salvar
        </button>
        <button onClick={() => setConfirm(true)}
          className="flex items-center gap-2 bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 px-4 py-2.5 rounded-xl text-sm">
          <Trash2 className="h-4 w-4" /> Apagar Conta Família
        </button>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-bold text-white mb-2">Apagar conta família?</h3>
            <p className="text-slate-400 text-sm mb-5">
              Todos os dados de participação e metas serão removidos. O cônjuge perderá o acesso.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(false)}
                className="flex-1 border border-slate-700 text-slate-400 rounded-xl py-2 text-sm">
                Cancelar
              </button>
              <button onClick={deletar}
                className="flex-1 bg-rose-600 text-white rounded-xl py-2 text-sm font-semibold">
                Sim, apagar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Página Principal ───────────────────────────────────────────────
export default function Familia() {
  const { user } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [familia, setFamilia]     = useState(null);
  const [loadingFam, setLoadingFam] = useState(true);
  const [showCriar, setShowCriar] = useState(false);
  const [aba, setAba]             = useState("painel");
  const [painel, setPainel]       = useState(null);
  const [loadingPainel, setLoadingPainel] = useState(false);

  const now  = new Date();
  const [mes, setMes]   = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [ano, setAno]   = useState(String(now.getFullYear()));

  // Buscar família do usuário
  useEffect(() => {
    (async () => {
      setLoadingFam(true);
      try {
        const { data } = await axios.get(`${API}/familia/minha`);
        setFamilia(data.familia);
      } catch {}
      finally { setLoadingFam(false); }
    })();
  }, []);

  // Buscar painel ao mudar mês/ano ou família
  const fetchPainel = useCallback(async () => {
    if (!familia?.familiaId) return;
    setLoadingPainel(true);
    try {
      const { data } = await axios.get(
        `${API}/familia/${familia.familiaId}/painel?mes=${mes}&ano=${ano}`);
      setPainel(data);
    } catch { toast.error("Erro ao carregar painel"); }
    finally { setLoadingPainel(false); }
  }, [familia?.familiaId, mes, ano]);

  useEffect(() => { fetchPainel(); }, [fetchPainel]);

  const membros = (familia?.membros || []).filter(m => m.userId);
  const ABAS = ["painel","gastos","entradas","saldos","metas","configurações"];

  if (!hasPremiumPlan(user)) return (
      <AppLayout>
        <PageHeader title="Família" />
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-full bg-amber-500/20 blur-2xl scale-[1.8]" />
            <div className="relative w-24 h-24 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,#1c1200,#111827)", border: "1.5px solid rgba(251,191,36,.25)" }}>
              <Lock className="w-10 h-10 text-amber-400" strokeWidth={1.5} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Recurso Exclusivo</h1>
          <p className="text-slate-400 text-sm max-w-sm mb-1">
            O módulo <strong className="text-white">Família</strong> é exclusivo do plano <strong className="text-amber-400">Anual Premium</strong>.
          </p>
          <p className="text-slate-500 text-xs max-w-xs mb-8">
            Gerencie as finanças do casal juntos, com receitas, despesas e saldos compartilhados em tempo real.
          </p>
          <button
            onClick={() => setUpgradeOpen(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-slate-900 transition-all hover:scale-105 active:scale-95"
            style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)", boxShadow: "0 4px 20px rgba(245,158,11,.35)" }}>
            <Gem className="w-4 h-4" /> Fazer Upgrade para Premium
          </button>
        </div>
        <PaymentModal open={upgradeOpen} onOpenChange={setUpgradeOpen} userEmail={user?.email} userName={user?.name} />
      </AppLayout>
  );

  if (loadingFam) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64 text-slate-500">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" /> Carregando...
      </div>
    </AppLayout>
  );

  // Sem família — tela de criação
  if (!familia) return (
    <AppLayout>
      <PageHeader title="Família" icon={<Home className="h-6 w-6" />} />
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
          <Users className="h-10 w-10 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Conta Família</h2>
        <p className="text-slate-400 max-w-md mb-8">
          Gerencie as finanças do casal juntos. Cada um com seu login,
          vendo receitas, despesas e saldos compartilhados em tempo real.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 w-full max-w-lg">
          {[
            { icon: DollarSign, label: "Receitas do casal consolidadas" },
            { icon: ArrowRightLeft, label: "Saldo de quem deve repassar" },
            { icon: Target, label: "Metas por categoria" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-sm text-slate-400">
              <Icon className="h-5 w-5 text-emerald-400 mb-2" />
              {label}
            </div>
          ))}
        </div>
        <button onClick={() => setShowCriar(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl font-semibold text-base">
          <Plus className="h-5 w-5" /> Criar Conta Família
        </button>
      </div>

      {showCriar && (
        <CriarFamiliaModal
          user={user}
          onClose={() => setShowCriar(false)}
          onCreated={(f) => { setFamilia(f); setShowCriar(false); }}
        />
      )}
    </AppLayout>
  );

  // Convite pendente — usuário é membro mas cônjuge ainda não aceitou
  const conjugePendente = familia.membros?.find(m => m.role === "membro" && m.status === "pendente");

  return (
    <AppLayout>
      <PageHeader
        title={familia.nome}
        icon={<Home className="h-6 w-6" />}
        actions={
          <div className="flex items-center gap-2">
            {/* Seletor Mês */}
            <select value={mes} onChange={e => setMes(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none">
              {MESES.map((m, i) => (
                <option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>
              ))}
            </select>
            {/* Seletor Ano */}
            <select value={ano} onChange={e => setAno(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none">
              {ANOS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button onClick={fetchPainel} disabled={loadingPainel}
              className="p-1.5 text-slate-400 hover:text-white transition">
              <RefreshCw className={`h-4 w-4 ${loadingPainel ? "animate-spin" : ""}`} />
            </button>
          </div>
        }
      />

      {/* Aviso convite pendente */}
      {conjugePendente && (
        <div className="mb-4 flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <Send className="h-4 w-4" />
            Convite pendente para <strong>{conjugePendente.nome}</strong> ({conjugePendente.email})
          </div>
          <button
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/familia/${familia.familiaId}/reenviar-convite`);
                navigator.clipboard.writeText(data.invite_link);
                toast.success("Link copiado! Compartilhe com o cônjuge.");
              } catch { toast.error("Erro ao reenviar convite"); }
            }}
            className="flex items-center gap-1.5 text-xs bg-amber-500/20 hover:bg-amber-500/40 text-amber-400 px-3 py-1.5 rounded-lg">
            <Copy className="h-3.5 w-3.5" /> Copiar link
          </button>
        </div>
      )}

      {/* Membros badge */}
      <div className="flex items-center gap-2 mb-5">
        {membros.map(m => (
          <div key={m.userId} className="flex items-center gap-1.5 bg-slate-800 rounded-full px-3 py-1">
            <Avatar nome={m.nome} role={m.role} />
            <span className="text-xs text-slate-300">{m.nome}</span>
            {m.role === "titular" && <Crown className="h-3 w-3 text-amber-400" />}
          </div>
        ))}
      </div>

      {/* Abas */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
        {ABAS.map(a => (
          <button key={a} onClick={() => setAba(a)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition
              ${aba === a ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
            {a.charAt(0).toUpperCase() + a.slice(1)}
          </button>
        ))}
      </div>

      {/* Conteúdo da aba */}
      {aba === "painel"        && <PainelTab painel={painel} familia={familia} />}
      {aba === "gastos"        && <GastosTab painel={painel} familiaId={familia.familiaId} membros={membros} onRefresh={fetchPainel} />}
      {aba === "entradas"      && <ReceitasTab painel={painel} />}
      {aba === "saldos"        && <SaldosTab familiaId={familia.familiaId} ano={ano} />}
      {aba === "metas"         && <MetasTab familiaId={familia.familiaId} painel={painel} />}
      {aba === "configurações" && (
        <ConfigTab
          familia={familia}
          familiaId={familia.familiaId}
          onUpdated={f => setFamilia(f)}
          onDeleted={() => setFamilia(null)}
        />
      )}
    </AppLayout>
  );
}
